import Client, { IOrbisClientResponse } from '@orbis-systems/omni-sdk-ts/dist/apis/orbis/client';
import { IEndpointRequest } from '@orbis-systems/omni-sdk-ts/dist/types/apis/core/endpoint';
import { GetUrl, IGetOrbisSDK, PostUrl } from '@orbis-systems/omni-sdk-ts/dist/types/apis/orbis';
import { isNil } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { ICache } from './Cache';
import { MiddlewareInjectToken } from './constants/tokens';
import { ApiPermissions, CacheLevel, MiddlewareConfig } from './types/index';

const API_NO_PERMISSION_RULES: Partial<
    Record<keyof ApiPermissions, { urls?: (GetUrl | PostUrl)[]; urlRegex?: RegExp; returnData: any }[]>
> = {
    advisor: [
        {
            urlRegex: /\/(branch|advisory)\/.*/,
            returnData: new Error('No advisor permission'),
        },
    ],
    rtbs: [
        {
            urls: ['v2/advisory/model/rtbs', 'v1/branch/rtbs', 'user/rtb/history'],
            returnData: [],
        },
        {
            urls: ['user/rtb'],
            returnData: {},
        },
    ],
    documents: [
        {
            urls: ['documents/service'],
            returnData: [],
        },
    ],
    iav: [
        {
            urls: ['iav/init'],
            returnData: { publicKey: '' },
        },
        {
            urls: ['iav/complete', 'transfer/relationship/cancel'],
            returnData: new Error('No IAV permission'),
        },
    ],
    transfers: [
        {
            urls: ['transfer/relationship/list', 'transfer/status'],
            returnData: [],
        },
        {
            urls: ['transfer/available'],
            returnData: 0,
        },
        {
            urlRegex: /\/transfer\/.*/,
            returnData: new Error('No transfer permission'),
        }
    ],
};

@injectable()
export class OrbisSdkWrapper implements IGetOrbisSDK {
    client: Client;

    constructor(
        @inject(MiddlewareInjectToken.OrbisSdk) private orbisSdk: IGetOrbisSDK,
        @inject(MiddlewareInjectToken.Config) private config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) private cache: ICache,
    ) {
        this.client = orbisSdk.client;
    }

    private checkPermission(url: GetUrl | PostUrl): {
        hasPermission: boolean;
        returnData?: any;
    } {
        const apis = Object.keys(this.config.apiPermissions) as (keyof ApiPermissions)[];
        for (const api of apis) {
            const rules = API_NO_PERMISSION_RULES[api];
            if (!this.config.apiPermissions[api] && rules) {
                for (const item of rules) {
                    if (item.urls?.includes(url) || item.urlRegex?.test(url)) {
                        if (item.returnData instanceof Error) {
                            throw item.returnData;
                        }

                        return {
                            hasPermission: false,
                            returnData: item.returnData,
                        };
                    }
                }
            }
        }

        return {
            hasPermission: true,
        };
    }

    get<T = any>(
        url: GetUrl,
        data?: any,
        config?: IEndpointRequest & { forceNoCache?: boolean },
    ): Promise<IOrbisClientResponse<T>> {
        const { hasPermission, returnData } = this.checkPermission(url);

        if (!hasPermission) {
            return Promise.resolve({ data: returnData } as IOrbisClientResponse<T>);
        }

        if (config?.forceNoCache || this.config.cacheLevel === CacheLevel.NoCache) {
            return this.orbisSdk.get(url, data, config);
        }

        const keyObject = {
            url,
            data,
            config,
        };

        const cacheKey = JSON.stringify(keyObject);
        const response = this.cache.get(cacheKey);

        if (!isNil(response)) {
            if (response instanceof Promise) {
                return response as Promise<IOrbisClientResponse>;
            } else {
                return Promise.resolve(response as IOrbisClientResponse);
            }
        } else {
            this.cache.del(cacheKey);

            const promise = this.orbisSdk.get(url, data, config);

            promise
                .then((response) => {
                    this.cache.set(cacheKey, response, this.config.cacheDuration);
                    return response;
                })
                .catch((error: Error) => {
                    this.cache.del(cacheKey);
                    throw error;
                });

            this.cache.set(cacheKey, promise, this.config.cacheDuration);
            return promise;
        }
    }

    post<T = any>(url: PostUrl, data?: any, config?: IEndpointRequest): Promise<IOrbisClientResponse<T>> {
        const { hasPermission, returnData } = this.checkPermission(url);

        if (!hasPermission) {
            return Promise.resolve({ data: returnData } as IOrbisClientResponse<T>);
        }

        return this.orbisSdk.post(url, data, config);
    }
}
